import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import SignInPageWrapper from './SignInPageWrapper';
import { motion } from 'framer-motion';
import styles from './SignIn.module.css';
import { debugLog, errorLog } from '../utils/debug';

export default function PaymentSignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, signInWithGoogle, currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // If auth is still loading, show nothing
  if (authLoading) {
    return null;
  }

  // If user is already authenticated, redirect to homepage
  if (currentUser) {
    return <Navigate to="/homepage" replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      // Redirect to homepage after successful authentication
      navigate('/homepage');
    } catch (error) {
      errorLog('Login error:', error);
      setError('Failed to sign in. Please check your email and password.');
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      // Redirect to homepage after successful authentication
      navigate('/homepage');
    } catch (error) {
      errorLog('Google sign in error:', error);
      setError('Failed to sign in with Google');
    }
    setLoading(false);
  }

  function togglePasswordVisibility() {
    setShowPassword(!showPassword);
  }

  function toggleRememberMe() {
    setRememberMe(!rememberMe);
  }

  return (
    <SignInPageWrapper>
      <div className={styles.signinContainer}>

        <div className={styles.phoneContainer}>          
          <div className={styles.content}>
            <motion.div 
              className={styles.welcomeTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Hey! Sign in with the account you just used to subscribe...
            </motion.div>

            <motion.div 
              className={styles.formContainer}
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {error && <div className={styles.errorMessage}>{error}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <input 
                    type="email" 
                    className={styles.inputField} 
                    placeholder="Enter your email..." 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.passwordContainer}>
                    <input 
                      type={showPassword ? "text" : "password"} 
                      className={styles.inputField} 
                      placeholder="Enter your password" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button 
                      type="button"
                      className={styles.passwordToggle} 
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="#12332F" className="size-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="#12332F" className="size-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.optionsRow}>
                  <div className={styles.rememberMe} onClick={toggleRememberMe}>
                    <div className={`${styles.checkbox} ${!rememberMe ? styles.unchecked : ''}`}>
                      <span className={styles.checkboxIcon}>✓</span>
                    </div>
                    <span className={styles.rememberText}>Remember me</span>
                  </div>
                  <a href="/resetpassword" className={styles.forgotPassword}>Forgot password?</a>
                </div>

                <button 
                  type="submit" 
                  className={styles.signinBtn} 
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              <motion.div 
                className={styles.divider}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                OR
              </motion.div>

              <motion.button 
                type="button"
                className={styles.gsiMaterialButton} 
                onClick={handleGoogleSignIn}
                disabled={loading}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className={styles.gsiMaterialButtonState}></div>
                <div className={styles.gsiMaterialButtonContentWrapper}>
                  <div className={styles.gsiMaterialButtonIcon}>
                    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{display: 'block'}}>
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                      <path fill="none" d="M0 0h48v48H0z"></path>
                    </svg>
                  </div>
                  <span className={styles.gsiMaterialButtonContents}>Sign in with Google</span>
                  <span style={{display: 'none'}}>Sign in with Google</span>
                </div>
              </motion.button>

              <motion.div 
                className={styles.signupLink}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                Don't have an account? <a href="https://useventy.com" target="_blank" rel="noopener noreferrer" className={styles.signupText}>Sign Up</a>
              </motion.div>
            </motion.div>
          </div>

          <div className={styles.safeArea}></div>
        </div>
      </div>
    </SignInPageWrapper>
  );
}
